/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.network.di

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.annotations.SupabaseInternal
import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.functions.Functions
import io.github.jan.supabase.postgrest.Postgrest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.http.HttpHeaders
import org.koin.dsl.module
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.core.network.auth.AuthTokenProvider
import studio.oddpods.cauce.core.network.auth.SupabaseAuthService
import studio.oddpods.cauce.core.network.auth.SupabaseAuthTokenProvider

val NetworkModule = module {
    @OptIn(SupabaseInternal::class)
    single<SupabaseClient> {
        val url = getProperty<String>("SUPABASE_URL")
        val key = getProperty<String>("SUPABASE_ANON_KEY")
        require(url.isNotBlank()) { "SUPABASE_URL is missing. Add it to local.properties." }
        require(key.isNotBlank()) { "SUPABASE_ANON_KEY is missing. Add it to local.properties." }

        // PHI safety: HTTP_LOG_LEVEL is opt-in per build. Default is NONE so
        // shipped builds never log bodies. Debug builds may set HEADERS to
        // see request shapes. ALL and BODY are explicitly forbidden because
        // patient records flow through this client and bodies would leak
        // PII/PHI into Logcat, Crashlytics, and browser consoles.
        val httpLogLevel = runCatching {
            LogLevel.valueOf(getProperty<String>("HTTP_LOG_LEVEL"))
        }.getOrDefault(LogLevel.NONE)
        require(httpLogLevel != LogLevel.ALL && httpLogLevel != LogLevel.BODY) {
            "HTTP_LOG_LEVEL=$httpLogLevel would log request bodies and leak PHI. " +
                "Use HEADERS or NONE."
        }

        createSupabaseClient(
            supabaseUrl = url,
            supabaseKey = key,
        ) {
            install(Auth)
            install(Postgrest)
            install(Functions)

            httpConfig {
                install(Logging) {
                    level = httpLogLevel
                    sanitizeHeader { header ->
                        header.equals(HttpHeaders.Authorization, ignoreCase = true) ||
                            header.equals(HttpHeaders.Cookie, ignoreCase = true) ||
                            header.equals(HttpHeaders.SetCookie, ignoreCase = true) ||
                            header.equals("apikey", ignoreCase = true)
                    }
                }
            }
        }
    }

    single<AuthService> { SupabaseAuthService(get()) }

    single<AuthTokenProvider> { SupabaseAuthTokenProvider(get()) }
}
